import {
  NotificationDigestCadence,
  NotificationPreferenceScope,
  SetNotificationPreferenceInput,
} from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";

/**
 * Draft state for the notification preferences page: every toggle position the
 * reader is currently looking at, and the diff against what the server said.
 *
 * All of it is pure. The page is a large grid of switches whose meaning depends
 * on three interacting layers (a per-notification choice, a channel parent and a
 * section parent), and the rules that matter most — "only send what was touched",
 * "a parent never moves the switches beneath it", "a muted section still shows its
 * stored positions" — are exactly the rules that are invisible in a rendered
 * screenshot. Keeping them in functions that take state and return state means they
 * can be asserted directly instead of through the DOM.
 *
 * The one hook at the bottom is deliberately thin: it holds a `useState` and
 * re-seeds it when the server answer changes. Everything it does with the state
 * is one of the functions above it.
 */

/** Delivery channels a preference row can carry a toggle for. */
export const NOTIFICATION_CHANNEL_IN_APP = "IN_APP";
export const NOTIFICATION_CHANNEL_EMAIL = "EMAIL";

/**
 * The channels the page draws a column for, in column order. One list, shared by
 * the rendering and by the delivery arithmetic: a page that counted a channel it
 * never drew, or drew one it never counted, would contradict its own header.
 */
export const PREFERENCE_ROW_CHANNELS = [
  NOTIFICATION_CHANNEL_IN_APP,
  NOTIFICATION_CHANNEL_EMAIL,
] as const;

/**
 * The fields this module reads off a catalog parent switch. Narrower than the
 * generated type on purpose: the logic depends on the switch's coordinates and
 * its position, so a test can state a case in four lines rather than
 * reconstructing a whole GraphQL payload.
 *
 * `kind` and `channel` are carried straight through to the save payload and are
 * never rebuilt — the sentinel format is storage detail the server owns.
 */
export type DraftParentSwitch = {
  kind: string;
  channel: string;
  enabled: boolean;
};

/** The fields this module reads off one channel's entry in a row. */
export type DraftRowPreference = {
  channel: string;
  /**
   * The stored per-notification choice with parents ignored: the toggle position.
   * Never `enabled`, which is the folded delivered value and would render every
   * switch off under a mute — and then save those falses over the stored choices.
   */
  ownEnabled: boolean;
  /** What the kind ships with, which is what the Standard preset restores. */
  defaultEnabled: boolean;
  /** How often this alert's email arrives. EMAIL channel only. */
  digestCadence?: NotificationDigestCadence | null;
};

export type DraftCatalogRow = {
  kind: string;
  /** Only the channels this kind supports. An absent channel gets no toggle. */
  preferences: readonly DraftRowPreference[];
};

export type DraftCatalogSection = {
  groupId: string;
  /** One per channel, matching the columns the section's switches sit in. */
  parentSwitches: readonly DraftParentSwitch[];
  rows: readonly DraftCatalogRow[];
};

/** The fields this module reads off the catalog's digest schedule. */
export type DraftEmailDigest = {
  /** The zone the server resolved. Displayed nowhere; kept so a save can diff it. */
  timeZone: string;
};

export type DraftCatalog = {
  channelParentSwitches: readonly DraftParentSwitch[];
  emailDigest: DraftEmailDigest;
  sections: readonly DraftCatalogSection[];
};

/**
 * What the delivery arithmetic below needs from a row: which channels exist at
 * all. The stored positions come from the draft rather than from the catalog,
 * which is the whole point — the count has to move as the reader flips switches,
 * not describe the answer the server gave.
 *
 * Split out from {@link DraftCatalogRow} so a rendering component, which carries
 * presentation fields the catalog type knows nothing about, can pass itself
 * straight in.
 */
export type DeliveryRow = {
  kind: string;
  preferences: readonly { channel: string }[];
};

export type DeliverySection = {
  parentSwitches: readonly DraftParentSwitch[];
  rows: readonly DeliveryRow[];
};

/** One toggle in the draft: where it lives, and where the reader has put it. */
export type DraftToggle = {
  kind: string;
  channel: string;
  enabled: boolean;
  /** EMAIL rows only. Separate from `enabled` because they are separate columns. */
  cadence?: NotificationDigestCadence;
};

/**
 * Every toggle on the page, keyed by its coordinates.
 *
 * A record rather than a Map so React sees a new object per edit without any
 * cloning ceremony, and so the key order is the order the catalog gave —
 * which is what makes the save payload deterministic.
 */
export type NotificationPreferenceDraft = Record<string, DraftToggle>;

/**
 * Coordinates of one toggle. Parents and rows share the keyspace because they
 * share it in storage too: a parent is a preference row with a sentinel kind, so
 * giving them separate keyspaces here would invent a distinction the platform
 * does not have.
 */
export function draftKey(kind: string, channel: string): string {
  return `${kind}::${channel}`;
}

/**
 * The draft as it stands the instant the server answer arrives — the snapshot
 * every "is this dirty" question is asked against.
 *
 * Parents are seeded from `enabled` because a parent's resolved position *is* its
 * own position; nothing sits above it. Rows are seeded from `ownEnabled` because
 * something does — seeding a row from the folded `enabled` would draw every switch
 * in a muted section as off and then save those falses over the choices the
 * sentinel rows exist to preserve.
 */
export function buildDraftFromCatalog(
  catalog: DraftCatalog | null | undefined
): NotificationPreferenceDraft {
  const draft: NotificationPreferenceDraft = {};
  if (!catalog) {
    return draft;
  }

  for (const section of catalog.sections) {
    for (const row of section.rows) {
      for (const preference of row.preferences) {
        draft[draftKey(row.kind, preference.channel)] = {
          kind: row.kind,
          channel: preference.channel,
          enabled: preference.ownEnabled,
          ...(preference.digestCadence ? { cadence: preference.digestCadence } : {}),
        };
      }
    }
  }

  // Every parent switch, seeded from the position the server resolved for it. Not
  // derived from the rows underneath: a parent that is off while its rows are on is
  // the state this whole page exists to express, and deriving would erase it on
  // first paint.
  const parents = [
    ...catalog.channelParentSwitches,
    ...catalog.sections.flatMap((section) => section.parentSwitches),
  ];
  for (const parent of parents) {
    draft[draftKey(parent.kind, parent.channel)] = {
      kind: parent.kind,
      channel: parent.channel,
      enabled: parent.enabled,
    };
  }

  return draft;
}

/**
 * The draft with one toggle moved. Every other entry is carried through
 * untouched, which is the whole reason a parent can be flipped without
 * disturbing the rows beneath it: flipping a parent writes one key, and the
 * dimming the reader then sees is computed rather than stored.
 */
export function setDraftToggle(
  draft: NotificationPreferenceDraft,
  kind: string,
  channel: string,
  enabled: boolean
): NotificationPreferenceDraft {
  const key = draftKey(kind, channel);
  // Carried through so switching off and on again keeps the reader's schedule.
  const cadence = draft[key]?.cadence;
  return {
    ...draft,
    [key]: { kind, channel, enabled, ...(cadence ? { cadence } : {}) },
  };
}

/** One choice from one menu, split back into the two stored columns by the setter. */
export const EMAIL_CADENCE_OFF = "OFF";

export type EmailCadenceChoice = NotificationDigestCadence | typeof EMAIL_CADENCE_OFF;

/** The email column's current position for one alert, folded for the control. */
export function draftEmailChoice(
  draft: NotificationPreferenceDraft,
  kind: string
): EmailCadenceChoice {
  const toggle = draft[draftKey(kind, NOTIFICATION_CHANNEL_EMAIL)];
  if (!toggle?.enabled) {
    return EMAIL_CADENCE_OFF;
  }
  return toggle.cadence ?? NotificationDigestCadence.Daily;
}

/** Choosing off leaves the cadence stored, so switching back restores it. */
export function setDraftEmailChoice(
  draft: NotificationPreferenceDraft,
  kind: string,
  choice: EmailCadenceChoice
): NotificationPreferenceDraft {
  const key = draftKey(kind, NOTIFICATION_CHANNEL_EMAIL);
  const existing = draft[key];
  const cadence = choice === EMAIL_CADENCE_OFF ? existing?.cadence : choice;
  return {
    ...draft,
    [key]: {
      kind,
      channel: NOTIFICATION_CHANNEL_EMAIL,
      enabled: choice !== EMAIL_CADENCE_OFF,
      ...(cadence ? { cadence } : {}),
    },
  };
}

/** The current position of one toggle, or false for a toggle the page has none of. */
export function draftValue(
  draft: NotificationPreferenceDraft,
  kind: string,
  channel: string
): boolean {
  return draft[draftKey(kind, channel)]?.enabled ?? false;
}

/**
 * Only the toggles whose position differs from the server's, in catalog order.
 *
 * This is what the save sends, and the "only" is load-bearing. Sending every
 * toggle would write a row for every notification on the page, turning values that
 * were inheriting from the organization (or from the kind's own policy) into frozen
 * personal overrides — so a later change to an organization default would stop
 * reaching anyone who had ever visited this page.
 *
 * Parent switches take part on exactly the same terms. A moved parent is one row to
 * write; the rows beneath it are untouched and therefore absent, which is precisely
 * how a mute silences without clearing.
 */
export function dirtyToggles(
  draft: NotificationPreferenceDraft,
  snapshot: NotificationPreferenceDraft
): DraftToggle[] {
  return Object.keys(draft).flatMap((key) => {
    const current = draft[key];
    const original = snapshot[key];
    if (!current || !original) {
      return [];
    }
    // Either column moving makes the row dirty, or a daily-to-weekly edit would be
    // silently discarded as "nothing to save".
    if (current.enabled === original.enabled && current.cadence === original.cadence) {
      return [];
    }
    return [current];
  });
}

/**
 * The mutation input for a save.
 *
 * `leadTimeDays` and `thresholdPercent` are deliberately omitted rather than
 * sent: the "When" column is read-only for now, so the page has no per-reader
 * timing to store, and sending the value it is *displaying* would convert a
 * number that was inherited into an override nobody chose.
 *
 * Omitting them is safe because the API treats an absent field as "leave the
 * stored value alone" and only a field sent as null as "clear it". A save that
 * moves one switch therefore cannot disturb a threshold or lead time already
 * stored on that row.
 *
 * `digestCadence` rides along on the EMAIL rows that carry one: schedule and on/off
 * are two columns of one row, so they travel as one entry rather than two that would
 * overwrite each other.
 */
export function buildSavePayload(
  toggles: readonly DraftToggle[],
  scope: NotificationPreferenceScope,
  timeZoneEntry?: DraftTimeZoneEntry | null
): SetNotificationPreferenceInput[] {
  // The time-zone entry carries the channel master's own `enabled`, so a visit that
  // both flipped that master and changed a cadence would otherwise send two entries for
  // the same row and leave the result depending on apply order.
  const supersededByTimeZoneEntry = (toggle: DraftToggle) =>
    timeZoneEntry !== undefined &&
    timeZoneEntry !== null &&
    toggle.kind === timeZoneEntry.kind &&
    toggle.channel === timeZoneEntry.channel;

  const entries: SetNotificationPreferenceInput[] = toggles
    .filter((toggle) => !supersededByTimeZoneEntry(toggle))
    .map((toggle) => ({
      scope,
      kind: toggle.kind,
      channel: toggle.channel,
      enabled: toggle.enabled,
      ...(toggle.channel === NOTIFICATION_CHANNEL_EMAIL && toggle.cadence
        ? { digestCadence: toggle.cadence }
        : {}),
    }));

  if (timeZoneEntry) {
    entries.push({
      scope,
      kind: timeZoneEntry.kind,
      channel: timeZoneEntry.channel,
      enabled: timeZoneEntry.enabled,
      digestTimeZone: timeZoneEntry.timeZone,
    });
  }
  return entries;
}

/**
 * Carries the reader's zone on the channel master, since cadence is measured in it.
 * Never sent on the organization tab: the browser's zone belongs to whichever admin
 * is saving, and storing it would set the send hour for all their colleagues.
 */
export type DraftTimeZoneEntry = {
  kind: string;
  channel: string;
  enabled: boolean;
  timeZone: string;
};

/**
 * The reader's own zone, as the browser reports it.
 *
 * Read at save time rather than stored, and sent only alongside a cadence the
 * reader actually chose. Sending it on load would rewrite the stored zone of
 * anyone who opened the page from a hotel — and would make the page dirty on
 * arrival, which is the one thing a settings page must never be.
 */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Whether one channel of one row would actually be delivered: its own position
 * ANDed with the two parents above it.
 *
 * The same fold the server applies at fan-out, computed from the draft so the page
 * agrees with itself before anything is saved. It is deliberately *not* what a
 * switch renders — a switch shows its own stored position — but it is what the
 * dimming, the off badge and the header count are derived from.
 *
 * A channel the row has no entry for cannot deliver at all, which is why that is
 * checked first rather than defaulted to on.
 */
export function isChannelDelivering({
  row,
  channel,
  draft,
  channelParentSwitches,
  sectionParentSwitches,
}: {
  row: DeliveryRow;
  channel: string;
  draft: NotificationPreferenceDraft;
  channelParentSwitches: readonly DraftParentSwitch[];
  sectionParentSwitches: readonly DraftParentSwitch[];
}): boolean {
  const supported = row.preferences.some((preference) => preference.channel === channel);
  if (!supported) {
    return false;
  }
  if (!isParentChannelOn(draft, channelParentSwitches, channel)) {
    return false;
  }
  if (!isParentChannelOn(draft, sectionParentSwitches, channel)) {
    return false;
  }
  return draftValue(draft, row.kind, channel);
}

/**
 * Whether the parent governing one channel is on, reading the draft so an unsaved
 * flip takes effect immediately.
 *
 * A channel with no parent switch at all is on: absence has to mean "nothing is
 * silencing this", the same rule the server applies to a missing row. Defaulting to
 * off would grey a whole column because a payload was one field short.
 */
export function isParentChannelOn(
  draft: NotificationPreferenceDraft,
  // Only the coordinates, never the position: the answer comes from the draft, so
  // a caller holding a switch without its server-side `enabled` — the row header,
  // which renders positions from the draft too — can still ask.
  parents: readonly { kind: string; channel: string }[],
  channel: string
): boolean {
  const parent = parents.find((entry) => entry.channel === channel);
  if (!parent) {
    return true;
  }
  return draftValue(draft, parent.kind, parent.channel);
}

/**
 * The kinds whose in-app delivery this save would switch on: delivering under the
 * draft, not delivering under the server's answer.
 */
export function newlyDeliveringKinds(
  sections: readonly DeliverySection[],
  draft: NotificationPreferenceDraft,
  snapshot: NotificationPreferenceDraft,
  channelParentSwitches: readonly DraftParentSwitch[]
): string[] {
  return sections.flatMap((section) =>
    section.rows
      .filter((row) => {
        const where = {
          row,
          channel: NOTIFICATION_CHANNEL_IN_APP,
          channelParentSwitches,
          sectionParentSwitches: section.parentSwitches,
        };
        return (
          isChannelDelivering({ ...where, draft }) &&
          !isChannelDelivering({ ...where, draft: snapshot })
        );
      })
      .map((row) => row.kind)
  );
}

/**
 * How many of a section's rows would actually fire — the count a section header
 * shows.
 *
 * Delivered rather than stored, so a parent mute takes it to zero: the number
 * answers "what will reach me", which is the question a reader is asking when they
 * glance at a section header. The stored choices are not lost and the switches below
 * still show them; the count simply stops claiming they are arriving.
 */
export function countDeliveringRows(
  section: DeliverySection,
  draft: NotificationPreferenceDraft,
  channelParentSwitches: readonly DraftParentSwitch[]
): number {
  return section.rows.filter((row) =>
    PREFERENCE_ROW_CHANNELS.some((channel) =>
      isChannelDelivering({
        row,
        channel,
        draft,
        channelParentSwitches,
        sectionParentSwitches: section.parentSwitches,
      })
    )
  ).length;
}

/**
 * Whether a section fires on one channel: `on` when every alert that supports it is
 * on, `off` when none will fire, `mixed` in between.
 *
 * The section's own parent row folds in, so a muted section reads `off` however its
 * alerts are stored. A section whose alerts all lack the channel reads `on`.
 */
export type SectionChannelState = "on" | "mixed" | "off";

export function sectionChannelState(
  section: DeliverySection,
  draft: NotificationPreferenceDraft,
  channel: string
): SectionChannelState {
  if (!isParentChannelOn(draft, section.parentSwitches, channel)) {
    return "off";
  }
  const supporting = section.rows.filter((row) =>
    row.preferences.some((preference) => preference.channel === channel)
  );
  const on = supporting.filter((row) => draftValue(draft, row.kind, channel));
  if (on.length === supporting.length) {
    return "on";
  }
  if (on.length === 0) {
    return "off";
  }
  return "mixed";
}

/**
 * Moves one channel of a whole section: the section's own parent row, and every alert
 * under it that supports the channel.
 *
 * The parent row goes too, so that turning a section on clears a mute above the
 * alerts it just set, and turning one off also covers kinds added to the section
 * later. Stored cadences ride through untouched.
 */
export function setDraftSectionChannel(
  draft: NotificationPreferenceDraft,
  section: DeliverySection,
  channel: string,
  enabled: boolean
): NotificationPreferenceDraft {
  let next = draft;

  const parent = section.parentSwitches.find((entry) => entry.channel === channel);
  if (parent) {
    next = setDraftToggle(next, parent.kind, parent.channel, enabled);
  }

  for (const row of section.rows) {
    if (row.preferences.some((preference) => preference.channel === channel)) {
      next = setDraftToggle(next, row.kind, channel, enabled);
    }
  }

  return next;
}

export type NotificationPreferenceDraftController = {
  /** Current toggle positions, which is what every switch on the page renders. */
  draft: NotificationPreferenceDraft;
  /** The server's answer, kept so "dirty" stays a comparison rather than a flag. */
  snapshot: NotificationPreferenceDraft;
  setToggle: (kind: string, channel: string, enabled: boolean) => void;
  /** Moves one alert's email schedule, which is its on/off position too. */
  setEmailChoice: (kind: string, choice: EmailCadenceChoice) => void;
  /** Moves a whole section's channel, alerts included; see {@link setDraftSectionChannel}. */
  setSectionChannel: (section: DeliverySection, channel: string, enabled: boolean) => void;
  /** Throws the reader's edits away and goes back to the server's answer. */
  revert: () => void;
  dirty: DraftToggle[];
  /** Everything unsaved. */
  dirtyCount: number;
  isDirty: boolean;
};

/**
 * Holds one tab's draft.
 *
 * Re-seeded whenever a new server answer arrives, which is what makes a save or
 * a reset settle: the mutation refetches, the catalog identity changes, and the
 * draft becomes the fresh answer with nothing left dirty. One of these exists per
 * tab, owned by the page rather than by the tab panel, so switching tabs is
 * never destructive.
 */
export function useNotificationPreferenceDraft(
  catalog: DraftCatalog | null | undefined
): NotificationPreferenceDraftController {
  const snapshot = useMemo(() => buildDraftFromCatalog(catalog), [catalog]);

  const [seededFrom, setSeededFrom] = useState<NotificationPreferenceDraft>(snapshot);
  const [draft, setDraft] = useState<NotificationPreferenceDraft>(snapshot);

  // Re-seeded during render rather than in an effect. An effect would commit one
  // paint of the reader's stale edits against the new server answer first, which
  // on this page means a flash of switches in the positions a save just replaced.
  if (seededFrom !== snapshot) {
    setSeededFrom(snapshot);
    setDraft(snapshot);
  }

  const setToggle = useCallback((kind: string, channel: string, enabled: boolean) => {
    setDraft((current) => setDraftToggle(current, kind, channel, enabled));
  }, []);

  const setEmailChoice = useCallback((kind: string, choice: EmailCadenceChoice) => {
    setDraft((current) => setDraftEmailChoice(current, kind, choice));
  }, []);

  const setSectionChannel = useCallback(
    (section: DeliverySection, channel: string, enabled: boolean) => {
      setDraft((current) => setDraftSectionChannel(current, section, channel, enabled));
    },
    []
  );

  const revert = useCallback(() => {
    setDraft(snapshot);
  }, [snapshot]);

  const dirty = useMemo(() => dirtyToggles(draft, snapshot), [draft, snapshot]);

  return {
    draft,
    snapshot,
    setToggle,
    setEmailChoice,
    setSectionChannel,
    revert,
    dirty,
    dirtyCount: dirty.length,
    isDirty: dirty.length > 0,
  };
}
